import React, { HTMLAttributes, ReactNode } from 'react';
import { createPortal } from 'react-dom';

type OverlaySpacing = 'compact' | 'roomy' | 'edge';

interface SafeViewportOverlayProps extends Omit<HTMLAttributes<HTMLDivElement>, 'onClick'> {
  children: ReactNode;
  onClose: () => void;
  closing?: boolean;
  closeOnBackdrop?: boolean;
  portalContainer?: Element | null;
  spacing?: OverlaySpacing;
  zIndexClassName?: string;
}

const SPACING_CLASSES: Record<OverlaySpacing, string> = {
  compact:
    'pl-[max(env(safe-area-inset-left),0.5rem)] pr-[max(env(safe-area-inset-right),0.5rem)] pb-[calc(env(safe-area-inset-bottom)+0.5rem)] pt-[calc(env(safe-area-inset-top)+0.5rem)] sm:pl-[max(env(safe-area-inset-left),1rem)] sm:pr-[max(env(safe-area-inset-right),1rem)] sm:pb-[calc(env(safe-area-inset-bottom)+1rem)] sm:pt-[max(5rem,calc(env(safe-area-inset-top)+1rem))]',
  roomy:
    'pl-[max(env(safe-area-inset-left),1rem)] pr-[max(env(safe-area-inset-right),1rem)] pb-[calc(env(safe-area-inset-bottom)+1rem)] pt-[calc(env(safe-area-inset-top)+1.5rem)] sm:pb-[calc(env(safe-area-inset-bottom)+1rem)] sm:pt-[max(5rem,calc(env(safe-area-inset-top)+1rem))]',
  edge:
    'px-0 pb-[env(safe-area-inset-bottom)] pt-[env(safe-area-inset-top)] sm:pl-[max(env(safe-area-inset-left),1rem)] sm:pr-[max(env(safe-area-inset-right),1rem)] sm:pb-[calc(env(safe-area-inset-bottom)+1rem)] sm:pt-[max(5rem,calc(env(safe-area-inset-top)+1rem))]',
};

/**
 * Canonical shell for app overlays.
 *
 * The shell always escapes page stacking contexts, tracks Safari's dynamic
 * viewport, and keeps interactive content clear of device safe areas. The
 * caller owns only the panel layout and whether it aligns top/center/bottom.
 */
const SafeViewportOverlay: React.FC<SafeViewportOverlayProps> = ({
  children,
  onClose,
  closing = false,
  closeOnBackdrop = true,
  portalContainer,
  spacing = 'compact',
  zIndexClassName = 'z-[60]',
  className = '',
  ...overlayProps
}) => {
  if (typeof document === 'undefined') return null;

  return createPortal(
    <div
      {...overlayProps}
      className={`fixed inset-0 flex min-h-0 overflow-hidden overscroll-none bg-black/70 ${SPACING_CLASSES[spacing]} ${zIndexClassName} ${closing ? 'animate-fade-out' : 'animate-fade-in'} ${className}`}
      onClick={(event) => {
        if (closeOnBackdrop && event.target === event.currentTarget) onClose();
      }}
    >
      {children}
    </div>,
    portalContainer ?? document.body,
  );
};

export default SafeViewportOverlay;
